import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { 
  createRoutesFromChildren, 
  matchRoutes, 
  useLocation, 
  useNavigationType 
} from 'react-router'

export function initSentry () {
  const dsn = import.meta.env.VITE_APP_SENTRY_DSN
  const environment = import.meta.env.MODE
  const release = import.meta.env.VITE_APP_SENTRY_RELEASE
  
  if (!dsn) {
    console.warn('Sentry DSN not found. Sentry will not be initialized.')
    return
  }

  Sentry.init({
    dsn,
    environment,
    release,
    
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    
    replaysSessionSampleRate: environment === 'production' ? 0.01 : 0.0,
    replaysOnErrorSampleRate: environment === 'production' ? 0.1 : 1.0,
    
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    
    beforeSend (event, hint) {
      const error = hint.originalException
      
      if (error && typeof error === 'object' && 'message' in error) {
        const message = error.message
        
        if (
          typeof message === 'string' && (
            message.includes('Loading CSS chunk') ||
            message.includes('Loading chunk') ||
            message.includes('Network Error') ||
            message.includes('ChunkLoadError')
          )
        ) {
          return null
        }
      }
      
      return event
    },
    
    initialScope: {
      tags: {
        component: 'frontend',
        framework: 'react',
        bundler: 'vite',
      },
    },
  })
}

export function createSentryRouterIntegration () {
  return Sentry.reactRouterV6BrowserTracingIntegration({
    useEffect,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
  })
}

export const captureUserFeedback = (feedback: {
  name?: string;
  email?: string;
  message: string;
}) => {
  Sentry.captureFeedback({
    name: feedback.name || 'Anonymous',
    email: feedback.email || '',
    message: feedback.message,
  })
}

export const setUserContext = (user: {
  id?: string;
  email?: string;
  username?: string;
}) => {
  Sentry.setUser(user)
}

export const addBreadcrumb = (message: string, category?: string, level?: 'info' | 'warning' | 'error') => {
  Sentry.addBreadcrumb({
    message,
    category: category || 'navigation',
    level: level || 'info',
    timestamp: Date.now() / 1000,
  })
}

export const captureException = Sentry.captureException
export const captureMessage = Sentry.captureMessage
